// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------


// Material UI
import {
  Box,
  Button,
  Fade,
  List,
  ListItem,
  ListItemText,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  Zoom,
} from "@mui/material";
import { styled } from '@mui/material/styles';

// Icons
import CircleIcon from '@mui/icons-material/Circle';
import CheckIcon from '@mui/icons-material/Check';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// Custom Components
import RenderIf from "../utils/RenderIf";

// Custom Scripts
import { useScreenIsShort, useScreenIsCompact, useScreenIsTiny } from "../../scripts/utils";



// ----------------------------------------------------------------------------
//   Video Instructions Components
// ----------------------------------------------------------------------------


const CustomStepIconRoot = styled('div')(({ theme }) => ({
  zIndex: 1,
}));


function CustomStepIcon({ icon: step, active, completed, className }) {

  // Set the icon size (smaller on short screens), and the glyph inside it at half that
  const compact   = useScreenIsCompact();
  const iconSize  = compact ? 32 : { xs: 32, md: 48 };
  const glyphSize = compact ? 16 : { xs: 16, md: 24 };

  // Determine color based on state
  const iconColor = completed ? '#4caf50' : active ? '#4447ad' : 'text.disabled';

  // Match icon to button for step
  const icons = {
    1: VideoCameraFrontIcon,
    2: SmartDisplayIcon,
    3: AssignmentTurnedInIcon,
  };
  const IconComponent = icons[ step ];

  // Position the icon over a background circle of the correct color
  return (
    <CustomStepIconRoot ownerState={{ completed, active }} className={className}>
      <Box sx={{ position: "relative", width: iconSize, height: iconSize }}>
        <CircleIcon sx={{ fontSize: iconSize, color: iconColor, transition: "color 300ms cubic-bezier(0.4, 0, 0.2, 1)" }} />

        {/* Use the selected icon component */}
        <Fade in={!completed} timeout={250} unmountOnExit>
          <div>
            <IconComponent
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: glyphSize,
                color: "white",
                marginLeft: step === 1 ? '2%' : '',
              }}
            />
          </div>
        </Fade>

        {/* If step is completed, replace with a check */}
        <Zoom in={completed} timeout={250} unmountOnExit>
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}>
            <CheckIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: glyphSize,
                color: "white",
              }}
            />
          </Box>
        </Zoom>
      </Box>
    </CustomStepIconRoot>
  );
}


/**
 * A single step in the video instructions stepper.
 *
 * Note: The `Stepper` component passes a set of props to all its direct `Step`
 * children, captured here as `...stepProps`.
 */
function VideoInstructionsStep({ title, instructions, currentStep, totalSteps, variant = 'full', ...stepProps }) {

  // Determine step state based on index comparison
  const stepIndex = stepProps.index;
  const isActive = stepIndex === currentStep;
  const isCompleted = stepIndex < currentStep;
  const isInactive = !isActive && !isCompleted;

  // Determine text color based on step state
  const textColor = isInactive ? 'text.disabled' : 'text.primary';

  // Component layout
  return (
    <Step {...stepProps} >

      {/* Step Title */}
      <StepLabel StepIconComponent={CustomStepIcon}>
        <Box sx={{ paddingLeft: '12px', color: textColor }}>
          {/* Step number: its own line normally, "Step N of M" when only one step is shown,
              and part of the title line in the compact layout */}
          <RenderIf condition={ variant !== 'compact' }>
            <Typography fontSize="0.9em">
              { variant === 'single' ? `Step ${stepProps.index + 1} of ${totalSteps}` : `Step ${stepProps.index + 1}:` }
            </Typography>
          </RenderIf>
          <Typography fontWeight="bold">
            { variant === 'compact' ? `${stepProps.index + 1}. ${title}` : title }
          </Typography>
        </Box>
      </StepLabel>

      {/* Step Instructions */}
      <RenderIf condition={ (instructions || []).length }>
        <StepContent>
          <List dense>
          {
            (instructions || []).map((content, instructionIndex) => (
              <ListItem key={instructionIndex}>
                <ListItemText primary={content} />
              </ListItem>
            ))
          }
          </List>
        </StepContent>
      </RenderIf>

    </Step>
  )
}


/**
 * The content for the video instructions card.
 *
 * Expects `steps` to have the following fields:
 *   - `title`
 *   - `instructions`
 */
export function VideoInstructions({ steps, currentStep, stepsComplete }) {

  // Check the height of the screen, for responsive layout
  const screenIsShort   = useScreenIsShort();
  const screenIsCompact = useScreenIsCompact();
  const screenIsTiny    = useScreenIsTiny();

  // Very short screens only have room for the current step
  const variant   = screenIsTiny ? 'single' : screenIsCompact ? 'compact' : 'full';
  const shownStep = Math.min(currentStep, steps.length - 1);

  return (
    <Stepper
      activeStep={ currentStep }
      orientation="vertical"
      // A lone step (variant 'single') would otherwise get a connector line above it
      { ...(variant === 'single' && { connector: null }) }
      sx={{
        // Control the vertical connector (line between steps)
        '& .MuiStepConnector-line': {
          minHeight: screenIsCompact ? 0 : screenIsShort ? "4px" : "8px",  // Minimum height of the connector line
          borderLeftWidth: 0,                        // Thickness of the connector line
        },
        '& .MuiStepLabel-root': {
          paddingLeft: '12px',
        },
      }}
    >
    {
      steps.map(( title, index ) => (variant !== 'single' || index === shownStep) && (
        // An explicit index keeps the step's number and state right when it's shown alone
        <VideoInstructionsStep
          key={ title } index={ index } title={ title } currentStep={ currentStep }
          totalSteps={ steps.length } variant={ variant }
        />
      ))
    }
    </Stepper>
  )
}



// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default {
  VideoInstructions,
}
